import type { Group, Object3D } from 'three';
import type { Vec2, Vec3, Ring } from '../geo/types';
import type { LinearRGB } from '../core/color';
import type { Layout, PaletteModel, AppData, CameraPose } from '../data/types';
import type { Rng } from '../core/rng';
import type { SiteModel } from '../site/types';
import type { CollisionWorld } from '../nav/types';
import type { MaterialFactory } from '../render/types';
import type { ChunkSink } from './mesh/chunker';
import type { PlannedLots } from './fabric/fabricLots';
import type { PavedGround } from './streetscape/pavingPieces';
import type { TrianglePlans } from './terrain/trianglePlans';
import type { WaterPlan } from './terrain/waterPlan';
import type { DayWindow } from '../time/daylight';
import type { HogeHuisYardPlan } from './fabric/hogeHuisYard';
import type { LifePlan } from './life/types';
import type { CitizenAsset } from './life/citizens/citizenAsset';
import type { Townspeople } from '../communication/types';
import type { RoofProfile } from './fabric/roofProfile';

export type ObstacleKind =
  'house' | 'landmark' | 'fortification' | 'windmill' | 'farm' | 'structure' | 'vessel';

export interface Obstacle {
  readonly id: string;
  readonly kind: ObstacleKind;
  readonly polygon: Ring;
}

/** A flame, or a pane with a flame behind it, after dark (render/night). */
export interface NightLight {
  /** World metres. */
  readonly position: Vec3;
  readonly colour: LinearRGB;
  /** Metres its light carries over the surfaces around it. */
  readonly reach: number;
  /** Metres across of the glow at the source itself. */
  readonly size: number;
  /** Radians its flicker runs ahead of every other flame's. */
  readonly phase: number;
  /**
   * The share of the evening's lights that must still burn for this one to burn (time/daylight.ts
   * burningShare). Absent on a light that burns the whole night.
   */
  readonly from?: number;
  /** Out of the wall, for a pane: its glow lies in the wall's plane and shows from the front only. */
  readonly outward?: Vec3;
  /** Width over height of a pane's glow; a flame's is round. */
  readonly aspect?: number;
  /**
   * The origin of the model the light rides on and that model's painter-view scale across and up
   * (ships' shipPlacement.ts painterScaleOf), so the light stays on a ship the painter view draws
   * larger than it is.
   */
  readonly rides?: { readonly origin: Vec3; readonly scale: readonly [number, number] };
}

export interface NightLightSource {
  /** Whether list() may return other lights, or the same ones elsewhere, from frame to frame. */
  readonly moving: boolean;
  list(): readonly NightLight[];
}

export interface Animated {
  readonly id: string;
  update(timeSeconds: number): void;
  /** `day` is the day's sunrise and sunset, for what keeps the day's hours (life/crowd). */
  setTimeOfDay?(minutes: number, activity: number, day?: DayWindow): void;
  readonly lights?: NightLightSource;
  readonly townspeople?: Townspeople;
}

export interface BuildContext {
  readonly layout: Layout;
  readonly palette: PaletteModel;
  readonly painterView: CameraPose;
  readonly site: SiteModel;
  /** The lots, planned once for every feature before any builds (lotPlan.ts). */
  readonly lots: PlannedLots;
  /** The paving, and the ground the ground feature draws around it (pavingPieces.ts). */
  readonly paving: PavedGround;
  /** Present only after the static world plan passes its source and input guards. */
  readonly hogeHuis?: HogeHuisYardPlan | undefined;
  readonly lifePlan?: LifePlan | undefined;
  /** Resident activity shares the cast's physical house doors. */
  readonly crowdDoors?: Set<string>;
  /** Populated by the fabric before the roof-life features build. */
  readonly roofs?: RoofProfile[];
  /** The citizens' figures and clips, with which the people of the city are drawn. */
  readonly citizens?: CitizenAsset | undefined;
  readonly terrainPlans?:
    | {
        readonly ground: TrianglePlans;
        readonly paving: TrianglePlans;
        readonly water?: WaterPlan;
      }
    | undefined;
  readonly rng: Rng;
  readonly materials: MaterialFactory;
  readonly chunks: ChunkSink;
  warn(message: string): void;
}

export interface FeatureOutput {
  readonly objects: readonly Object3D[];
  readonly obstacles: readonly Obstacle[];
  /** What stands in the water and stops a boat, never a walker: hulls and the harbour's works. */
  readonly afloat?: readonly Obstacle[];
  readonly animated: readonly Animated[];
  readonly counts: Readonly<Record<string, number>>;
}

export interface WorldFeature {
  readonly id: string;
  readonly label: string;
  readonly weight: number;
  build(ctx: BuildContext): FeatureOutput;
  /** Spatial jobs for responsive browser loading; build remains available for isolated checks. */
  batches?(
    ctx: BuildContext
  ): readonly { readonly label: string; readonly build: () => FeatureOutput }[];
}

export interface WorldStats {
  readonly seed: number;
  readonly buildMs: number;
  readonly stageMs: Readonly<Record<string, number>>;
  readonly counts: Readonly<Record<string, number>>;
  readonly meshes: number;
  readonly triangles: number;
  readonly geometryBytes: number;
  readonly warnings: readonly string[];
}

export interface World {
  readonly lifePlan: LifePlan;
  readonly root: Group;
  readonly site: SiteModel;
  readonly collision: CollisionWorld;
  /** Everything the features stand on the ground, and in the water, for the boat's own world. */
  readonly obstacles: readonly Obstacle[];
  readonly afloat: readonly Obstacle[];
  readonly animated: readonly Animated[];
  readonly spawn: Vec2;
  readonly stats: WorldStats;
  dispose(): void;
}

export interface WorldInput {
  readonly data: AppData;
  readonly seed: number;
  readonly materials: MaterialFactory;
  /** The citizens' file (life/citizens/citizenAsset.ts); a world built without it has no people
   *  drawn as citizens. */
  readonly citizens?: CitizenAsset | undefined;
}

export interface ProgressReporter {
  run<T>(label: string, weight: number, work: () => T): Promise<T>;
  readonly fraction: number;
}

export declare function createProgressReporter(
  totalWeight: number,
  onChange: (fraction: number, label: string) => void,
  yieldToUi: () => Promise<void>
): ProgressReporter;

export declare function buildWorld(input: WorldInput, progress: ProgressReporter): Promise<World>;

export function emptyFeatureOutput(): FeatureOutput {
  return { objects: [], obstacles: [], animated: [], counts: {} };
}
